import { TableColumnsType } from "antd";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import exportTable from "../../../lib/exportTable";
import { IEnrolledStudentByWeek } from "../../../models/statistics.model";
import { RootState } from "../../../store/rootReducer";
import DownloadButton from "../../DownloadButton";
import moment from "moment";

interface IProps {
  res: any;
  learningYearCode: string|undefined;
}

const TableContainer: FC<IProps> = ({
  res,
  learningYearCode,  
}) => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const [data, setData] = useState<any>([]);

  useEffect(() => {
    if (res.length) {
      let _data: any = [];
      // @ts-ignore
      res.at(0).data.forEach(campus => {
        campus.weeklyInfo.forEach((week: any) => {
          const item: any = {
            campusCode: campus.campusCode,
            campusEngName: campus.campusEngName,
            campusKorName: campus.campusKorName,
            weekCode: week.weekCode?.trim(),
            reCount: week.reCount,
            s1Count: week.s1Count,
            s2Count: week.s2Count,
          }

          const prevYearData = res.at(1)?.data?.find((prevCampus: any) => prevCampus.campusCode === campus.campusCode)?.weeklyInfo?.find((prevWeek: any) => prevWeek.weekCode?.trim() === week.weekCode?.trim());
          if (prevYearData) {
            item.prevReCount = prevYearData.reCount;
            item.prevS1Count = prevYearData.s1Count;
            item.prevS2Count = prevYearData.s2Count;
            item.reDiffPercent = Math.round((item.reCount - item.prevReCount) / item.prevReCount * 100);
            item.s1DiffPercent = Math.round((item.s1Count - item.prevS1Count) / item.prevS1Count * 100);
            item.s2DiffPercent = Math.round((item.s2Count - item.prevS2Count) / item.prevS2Count * 100);
          } else {
            item.prevReCount = null;
            item.prevS1Count = null;
            item.prevS2Count = null;
            item.reDiffPercent = null;
            item.s1DiffPercent = null;
            item.s2DiffPercent = null;
          }

          _data.push(item);
        });
      });

      if (auth.hqYn === 'Y') {
        _data = calculateTotal(_data);
      }
      setData(_data);
    }
  }, [res]);

  const calculateTotal = (_data: any) => {
    // @ts-ignore
    const weeks: string[] = _.uniqBy(_data, 'weekCode').map(item => item.weekCode);
    weeks.forEach(week => {
      const total: any = {
        campusCode: 'total',
        campusEngName: 'Total',
        campusKorName: t('statistics.attendance.3'),
        weekCode: week,
        reCount: _.sumBy(_data.filter((item: any) => item.weekCode === week), 'reCount'),
        s1Count: _.sumBy(_data.filter((item: any) => item.weekCode === week), 's1Count'),
        s2Count: _.sumBy(_data.filter((item: any) => item.weekCode === week), 's2Count'),
        prevReCount: _.sumBy(_data.filter((item: any) => item.weekCode === week), 'prevReCount'),
        prevS1Count: _.sumBy(_data.filter((item: any) => item.weekCode === week), 'prevS1Count'),
        prevS2Count: _.sumBy(_data.filter((item: any) => item.weekCode === week), 'prevS2Count'),
      };

      if (total.prevReCount) {
        total.reDiffPercent = Math.round((total.reCount - total.prevReCount) / total.prevReCount * 100);
      } else {
        total.reDiffPercent = null;
      }

      if (total.prevS1Count) {
        total.s1DiffPercent = Math.round((total.s1Count - total.prevS1Count) / total.prevS1Count * 100);
      } else {
        total.s1DiffPercent = null;
      }

      if (total.prevS2Count) {
        total.s2DiffPercent = Math.round((total.s2Count - total.prevS2Count) / total.prevS2Count * 100);
      } else {
        total.s2DiffPercent = null;
      }

      _data.push(total);
    });

    return _data;
  }

  const onExportTable = () => {
    const _data: any = _.cloneDeep(data);
    _data.forEach((item: any) => {
      item.reCount = item.reCount || 0;
      item.s1Count = item.s1Count || 0;
      item.s2Count = item.s2Count || 0;
      item.prevReCount = item.prevReCount || 0;
      item.prevS1Count = item.prevS1Count || 0;
      item.prevS2Count = item.prevS2Count || 0;
      item.reDiffPercent = item.reDiffPercent || '-';
      item.s1DiffPercent = item.s1DiffPercent || '-';
      item.s2DiffPercent = item.s2DiffPercent || '-';
    });
    const currentDate = moment(new Date());
    exportTable(columns, _data, `${t('statistics.studentEnrolledWeek.1')}_(${currentDate.format('YYYYMMDD')})`, t('statistics.studentEnrolledWeek.1'), [12, 10, 15, 15, 15, 15, 15, 15, 15, 15, 15]);
  }

  const columns: TableColumnsType<IEnrolledStudentByWeek> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      key: 'campusKorName',
      align: 'center',
    },
    {
      title: t('statistics.studentEnrollWeek.column1'),
      dataIndex: 'weekCode',
      key: 'weekCode',
      align: 'center',
    },
    {
      title: `(${learningYearCode}) ${t('statistics.studentEnrollMonth.column1')}`,
      dataIndex: 'reCount',
      key: 'reCount',
      align: 'center',
    },
    {
      title: `(${learningYearCode}) ${t('statistics.studentEnrollMonth.column2')}`,
      dataIndex: 's1Count',
      key: 's1Count',
      align: 'center',
    },
    {
      title: `(${learningYearCode}) ${t('statistics.studentEnrollMonth.column3')}`,
      dataIndex: 's2Count',
      key: 's2Count',
      align: 'center',
    },
    {
      title: `(${Number(learningYearCode) - 1}) ${t('statistics.studentEnrollMonth.column1')}`,
      dataIndex: 'prevReCount',
      key: 'prevReCount',
      align: 'center',
    },
    {
      title: `(${Number(learningYearCode) - 1}) ${t('statistics.studentEnrollMonth.column2')}`,
      dataIndex: 'prevS1Count',
      key: 'prevS1Count',
      align: 'center',
    },
    {
      title: `(${Number(learningYearCode) - 1}) ${t('statistics.studentEnrollMonth.column3')}`,
      dataIndex: 'prevS2Count',
      key: 'prevS2Count',
      align: 'center',
    },
    {
      title: `${t('statistics.studentEnrollMonth.column4')}`,
      dataIndex: 'reDiffPercent',
      key: 'reDiffPercent',
      align: 'center',
    },
    {
      title: `${t('statistics.studentEnrollMonth.column5')}`,
      dataIndex: 's1DiffPercent',
      key: 's1DiffPercent',
      align: 'center',
    },
    {
      title: `${t('statistics.studentEnrollMonth.column6')}`,
      dataIndex: 's2DiffPercent',
      key: 's2DiffPercent',
      align: 'center',
    },
  ];

  return (
    <>
      <div className="comm-table__area line-type">
        <table>
          <thead>
            <tr>
              <th rowSpan={2}>{t('common.menu.campus')}</th>
              <th rowSpan={2}>{t('statistics.studentEnrollWeek.column1')}</th>
              <th colSpan={3}>{learningYearCode}</th>
              <th colSpan={3}>{Number(learningYearCode) - 1}</th>
              <th colSpan={3}>{t('statistics.studentEnrollMonth.column7')}</th>
            </tr>
            <tr>
              <th>{t('statistics.studentEnrollMonth.column1')}</th>
              <th>{t('statistics.studentEnrollMonth.column2')}</th>
              <th>{t('statistics.studentEnrollMonth.column3')}</th>
              <th>{t('statistics.studentEnrollMonth.column1')}</th>
              <th>{t('statistics.studentEnrollMonth.column2')}</th>
              <th>{t('statistics.studentEnrollMonth.column3')}</th>
              <th>{t('statistics.studentEnrollMonth.column1')}</th>
              <th>{t('statistics.studentEnrollMonth.column2')}</th>
              <th>{t('statistics.studentEnrollMonth.column3')}</th>
            </tr>
          </thead>
          <tbody>
          {data.map((item: any) => (
            <tr className={item.campusCode === 'total' ? 'tfoot' : ''}>
              <td>{item.campusKorName}</td>
              <td>{item.weekCode}</td>
              <td>{item.reCount || 0}</td>
              <td>{item.s1Count || 0}</td>
              <td>{item.s2Count || 0}</td>
              <td>{item.prevReCount || 0}</td>
              <td>{item.prevS1Count || 0}</td>
              <td>{item.prevS2Count || 0}</td>
              <td>{item.reDiffPercent || '-'}</td>
              <td>{item.s1DiffPercent || '-'}</td>
              <td>{item.s2DiffPercent || '-'}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <div className="comm-bx__space">
        <DownloadButton onClick={onExportTable} />
      </div>
    </>
  );
}

export default TableContainer;
